import {DurationProps} from "../../types-enums/activity-common-properties";
import {getDaysCountBetweenDates} from "../common";
import {ACTIVITY_TYPE, AllActivityTypes} from "../../types-enums/activity";
import {
    isActivityCarBusState,
    isActivityHotelState,
    isCarBusCalculationRequest,
    isHotelCalculationRequest
} from "../check/type-guards";
import {DateTime} from "luxon";
import {UserProps, WindowedUserProps} from "../../api/model/user";
import {Customer} from "../../api/model/customer";

/**
 * Calculates the duration in either days or nights based on the provided input.
 *
 * @param {DurationProps} duration - The duration object containing information to calculate the result.
 * @param {boolean} isNights - Determines whether to calculate duration in nights. If true, returns the duration in nights; otherwise, returns the duration in days.
 * @returns {number|null} The calculated duration based on the dates or the `daysOrNights` property.
 */
export const getDurationInDaysOrNights = (duration: DurationProps, isNights: boolean): number | null => {
    const {dates, daysOrNights} = duration
    if (dates) {
        const {startDate, endDate,} = dates
        const countBetweenDates = getDaysCountBetweenDates(startDate, endDate)
        if (isNights) return countBetweenDates
        return countBetweenDates + 1
    }
    return daysOrNights
}


/**
 * Retrieves the full name of a user by combining their first and last names.
 *
 * @param {user: UserProps | WindowedUserProps | Customer | null} user - The user object containing first and last name properties,
 * or null if there is no user.
 * @returns {string | null} The concatenated full name as a string if the user exists,
 * or null if the input is null.
 */
export const getFullName = (user: UserProps | WindowedUserProps | Customer | null): string | null => `${user?.firstName} ${user?.lastName}`


/**
 * Retrieves the activity duration from the provided activity object based on its type.
 *
 * The duration is calculated differently depending on the specific activity type:
 * - For flights, a fixed duration of `1` is returned.
 * - For hotels, the duration is determined by the `numberOfNights` property if the activity meets the necessary state and request conditions.
 * - For car/bus rentals, the duration is determined by the `daysOfUse` property if the activity meets the necessary state and request conditions.
 *
 * If the activity type does not match any of the predefined cases or the required conditions are not satisfied, the function returns `null`.
 *
 * @param {AllActivityTypes} activity - The activity object containing information about the activity and its type.
 * @returns {number | null} The duration of the activity or `null` if the duration cannot be determined.
 */
export const getActivityDurationUsedInRequest = (activity: AllActivityTypes): number | null => {
    const {common: {activityType}} = activity
    switch (activityType) {
        case ACTIVITY_TYPE.flight:
            return 1
        case ACTIVITY_TYPE.hotel:
            if (
                isActivityHotelState(activity) &&
                activity.common.request &&
                isHotelCalculationRequest(activity.common.request[0])
            ) return activity.common.request[0].numberOfNights
            break
        case ACTIVITY_TYPE.car_bus:
            if (
                isActivityCarBusState(activity) &&
                activity.common.request &&
                isCarBusCalculationRequest(activity.common.request[0])
            ) return activity.common.request[0].daysOfUse
            break
        default:
            activityType satisfies never
    }
    return null
}

/**
 * Calculates the duration of an activity based on its type.
 *
 * @param {AllActivityTypes | null} activity - The activity object containing details such as activity type and duration information.
 *                                             Pass `null` if no activity is provided.
 * @returns {number | null} - Returns the duration of the activity in days or nights based on the type of activity.
 *                            Returns `null` if the activity is `flight`, if the activity object is `null`,
 *                            or if it's an unrecognized or unsupported activity type.
 *
 * @throws {TypeError} - May throw an error if the activity object structure does not conform to the expected type checkers (e.g., `isActivityHotelState` or `isActivityCarBusState`).
 *
 * Activity Types:
 * - `ACTIVITY_TYPE.flight`: Always returns `null` as no duration can be associated.
 * - `ACTIVITY_TYPE.hotel`: Calculates and returns the duration in nights based on the `hotelStayDuration` property.
 * - `ACTIVITY_TYPE.car_bus`: Returns the count of days as stored in the `countDays` property.
 * - Any other unrecognized `activityType` results in `null`.
 *
 * Note: This function relies on type-checking utility functions such as `isActivityHotelState` and `isActivityCarBusState`
 *       to validate the activity structure before calculating the duration.
 */
export const getActivityDuration = (activity: AllActivityTypes | null): number | null => {
    if (activity === null) return null
    const {common: {activityType}} = activity
    switch (activityType) {
        case ACTIVITY_TYPE.flight:
            return null
        case ACTIVITY_TYPE.hotel:
            if (isActivityHotelState(activity)) return getDurationInDaysOrNights(activity.hotelStayDuration, true)
            break
        case ACTIVITY_TYPE.car_bus:
            if (isActivityCarBusState(activity)) return activity.countDays
            break
        default:
            activityType satisfies never
    }
    return null
}

/**
 * Retrieves the minimum start date and maximum end date from an array of durations.
 *
 * @param {DurationProps[]} durations - An array of objects representing durations.
 * Each object contains a `dates` property with `startDate` and `endDate` as properties.
 *
 * @returns {{minStartDate: DateTime | null, maxEndDate: DateTime | null}} - An object containing
 * the earliest start date (`minStartDate`) and the latest end date (`maxEndDate`) found in the input array.
 * If no valid dates are found, both properties will be null.
 */
export const getMinMaxDates = (durations: DurationProps[]): {
    minStartDate: DateTime | null;
    maxEndDate: DateTime | null;
} => {
    let minStartDate: DateTime | null = null;
    let maxEndDate: DateTime | null = null;
    for (const duration of durations) {
        if (duration.dates) {
            const {startDate, endDate} = duration.dates
            if (!minStartDate || startDate < minStartDate) minStartDate = startDate
            if (!maxEndDate || endDate > maxEndDate) maxEndDate = endDate;
        }
    }
    return {minStartDate, maxEndDate};
}

/**
 * Calculates the minimum and maximum values of the `daysOrNights` property
 * from an array of `DurationProps` objects.
 *
 * @function
 * @param {DurationProps[]} durations - Array of objects containing a `daysOrNights` property.
 * @returns {Object} An object with two properties:
 * - `minDaysOrNights` {number|null} - The minimum value of `daysOrNights` found, or null if none are valid.
 * - `maxDaysOrNights` {number|null} - The maximum value of `daysOrNights` found, or null if none are valid.
 */
export const getMinMaxDaysOrNights = (durations: DurationProps[]) => {
    let minDaysOrNights: number | null = null
    let maxDaysOrNights: number | null = null
    for (const duration of durations) {
        if (duration.daysOrNights !== null) {
            if (minDaysOrNights === null || duration.daysOrNights < minDaysOrNights) minDaysOrNights = duration.daysOrNights;
            if (maxDaysOrNights === null || duration.daysOrNights > maxDaysOrNights) maxDaysOrNights = duration.daysOrNights;
        }
    }

    return {minDaysOrNights, maxDaysOrNights};
}